import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import AdminLayout from "../../../layouts/AdminLayout";
import {
  Button,
  Modal,
  SelectDropdown,
  SearchInput,
  LoadingSkeleton,
} from "../../../components/ui";
import UnitForm from "../../../components/societies/UnitForm";
import { toast } from "react-toastify";
import { mergeUnits, unmergeUnits } from "../../../services/unitService";
import BulkImport from "../../../components/BulkImport";
import { Check, Plus, Upload, ChevronUp, ChevronDown } from "lucide-react";
import styles from "./UnitManagement.module.css";


import {
  getTowersBySociety,
  getSocieties,
} from "../../../services/societyService";

import {
  getFlatsBySociety,
  getFlatById,
} from "../../../services/flatsService";
import { formatFloorName } from "../../../utils/propertyStructure";

const UnitManagement = () => {
  const [societies, setSocieties] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedSocietyId = searchParams.get("societyId") || "";

  const selectedSociety = useMemo(
    () => societies.find((s) => String(s.society_id) === String(selectedSocietyId)) || null,
    [societies, selectedSocietyId]
  );

  const [towers, setTowers] = useState([]);
  const [flats, setFlats] = useState([]);

  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);

  const [activeTowerId, setActiveTowerId] = useState(null);
  const [openFloor, setOpenFloor] = useState(null);

  const [search, setSearch] = useState("");

  const [selectedFlat, setSelectedFlat] = useState(null);
  const [flatModalOpen, setFlatModalOpen] = useState(false);

  const [mergeFloorId, setMergeFloorId] = useState(null);
  const [mergeSelected, setMergeSelected] = useState(new Set());

  const [importOpen, setImportOpen] = useState(false);

  // ================= FETCH =================
  const fetchSocieties = useCallback(async () => {
    const data = await getSocieties();
    setSocieties(data || []);

    if ((!selectedSocietyId || selectedSocietyId === "null") && data?.length) {
      setSearchParams({ societyId: String(data[0].society_id) });
    }
  }, [selectedSocietyId, setSearchParams]);

  const fetchData = useCallback(async () => {
    if (!selectedSocietyId) return;

    setLoading(true);

    const [towerData, flatData] = await Promise.all([
      getTowersBySociety(selectedSocietyId),
      getFlatsBySociety(selectedSocietyId),
    ]);

    setTowers(towerData || []);
    setFlats(flatData || []);

    if (towerData?.length) {
      setActiveTowerId(towerData[0].tower_id);
    }

    setLoading(false);
  }, [selectedSocietyId]);

  useEffect(() => {
    fetchSocieties();
  }, [fetchSocieties]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // ================= FILTER =================
  
  const filteredFlats = useMemo(() => {
    return flats
      .filter((f) => f.tower_id === activeTowerId)
      // When a unit is merged, hide the individual member flats and only show the merged unit itself.
      // Member flats are marked `is_merged=true`, while the merged "display" flat uses `unit_type` like "Jodi"/"MERGED".
      .filter((f) => {
        const unitType = String(f.unit_type || "").trim();
        const isMergedDisplay = unitType === "Jodi" || unitType === "MERGED";

        // Prefer server-computed membership flag (covers migrated/legacy data).
        if (Boolean(f.is_merge_member) && !isMergedDisplay) return false;

        // Fallback: Hide merged members even if backend doesn't send membership consistently.
        const hasMergeMarker =
          Boolean(f.merged_unit_id) ||
          Boolean(f.is_merged) ||
          (f.merged_from != null && String(f.merged_from).trim() !== "");
        if (hasMergeMarker && !isMergedDisplay) return false;
        return true;
      })
      .filter((f) => {
        const q = search.toLowerCase();
        return (
          f.flat_number?.toString().includes(q) ||
          f.unit_type?.toLowerCase().includes(q) ||
          String(f.floor_number).includes(q)
        );
      });
  }, [flats, activeTowerId, search]);

  // ================= GROUP =================
  const groupedFloors = useMemo(() => {
    const map = {};

    filteredFlats.forEach((f) => {
      const floor = Number(f.floor_number);
      if (!map[floor]) map[floor] = [];
      map[floor].push(f);
    });

    return map;
  }, [filteredFlats]);

  const floors = Object.keys(groupedFloors).sort(
    (a, b) => Number(a) - Number(b)
  );

  // ================= HELPERS =================
  const selectedUnits = flats.filter((f) =>
    mergeSelected.has(f.flat_id)
  );

  const toggleMergeSelection = (flat) => {
    if (flat.unit_type === "Jodi") return;

    if (flat.is_merged) {
      toast.info("Already merged");
      return;
    }

    if (!mergeFloorId) setMergeFloorId(flat.floor_id);

    if (mergeFloorId && mergeFloorId !== flat.floor_id) {
      setMergeFloorId(flat.floor_id);
      setMergeSelected(new Set([flat.flat_id]));
      return;
    }

    setMergeSelected((prev) => {
      const next = new Set(prev);
      next.has(flat.flat_id)
        ? next.delete(flat.flat_id)
        : next.add(flat.flat_id);
      return next;
    });
  };

  const doMergeSelected = async () => {
    const ids = Array.from(mergeSelected);
    if (ids.length < 2) return;

    await mergeUnits({ flat_ids: ids });

    setMergeSelected(new Set());
    setMergeFloorId(null);
    fetchData();
  };

  const handleOpenFlat = async (flat) => {
    setFlatModalOpen(true);
    const data = await getFlatById(flat.flat_id);
    setSelectedFlat(data);
  };

  // ================= UI =================
  return (
    
    <AdminLayout title="Unit & Flat Management">
      <div className={styles.container}>

        {/* HEADER */}
        <div className={styles.header}>
          <h2 className={styles.title}>Unit Management</h2>

          <div className={styles.toolbar}>
            <Button
              variant="outline"
              size="sm"
              icon={<Upload size={15} />}
              onClick={() => setImportOpen(true)}
            >
              Import Units
            </Button>
            <Button
              variant="primary"
              size="sm"
              icon={<Plus size={15} />}
              onClick={() => setOpen(true)}
            >
              Generate / Update Units
            </Button>
          </div>
        </div>

        {/* SOCIETY */}
        <div style={{ maxWidth: "340px", marginBottom: "16px" }}>
          <SelectDropdown
            value={selectedSocietyId}
            onChange={(e) => {
              setSearchParams({ societyId: e.target.value });
              setMergeSelected(new Set());
              setMergeFloorId(null);
            }}
            options={societies.map((s) => ({
              value: String(s.society_id),
              label: s.society_name,
            }))}
            placeholder="Select Property..."
            isClearable={false}
            isSearchable
          />
        </div>

        {/* TOWER TABS */}
        <div className={styles.tabs}>
          {towers.map((t) => (
            <Button
              key={t.tower_id}
              variant={activeTowerId === t.tower_id ? "primary" : "outline"}
              size="sm"
              onClick={() => {
                setActiveTowerId(t.tower_id);
                setMergeSelected(new Set());
                setMergeFloorId(null);
                setOpenFloor(null);
              }}
            >
              Tower - {t.tower_name}
            </Button>
          ))}
        </div>

        {/* SEARCH */}
        <div style={{ marginBottom: "16px" }}>
          <SearchInput
            placeholder="Search unit / floor / type..."
            value={search}
            onChange={(val) => setSearch(val)}
          />
        </div>

        {/* MERGE BAR */}
        {mergeSelected.size > 0 && (
          <div className={styles.mergeBar}>
            <div className={styles.mergeItems}>
              {selectedUnits.map((u) => (
                <div key={u.flat_id} className={styles.mergeItem}>
                  {u.flat_number}
                </div>
              ))}
            </div>

            <Button variant="primary" size="sm" onClick={doMergeSelected}>
              Merge ({mergeSelected.size})
            </Button>
          </div>
        )}

        {/* FLOORS */}
        {loading ? (
          <div className={styles.floorsRow}>
            {Array.from({ length: 4 }).map((_, fIdx) => (
              <div key={fIdx} className={styles.floorCard} style={{ pointerEvents: 'none' }}>
                <div className={styles.floorHeader}>
                  <div className={styles.unitSkeletonShimmer} style={{ width: '85px', height: '16px' }} />
                  <div className={styles.unitSkeletonShimmer} style={{ width: '16px', height: '16px', borderRadius: '50%' }} />
                </div>
                <div className={styles.units}>
                  {Array.from({ length: 6 }).map((_, uIdx) => (
                    <div key={uIdx} className={styles.unitChip} style={{ pointerEvents: 'none' }}>
                      <div className={styles.unitSkeletonShimmer} style={{ width: '42px', height: '14px' }} />
                      <div className={styles.unitSkeletonShimmer} style={{ width: '65px', height: '16px', borderRadius: '999px', marginTop: '16px' }} />
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className={styles.floorsRow}>
            {floors.map((floorId) => {
              const isOpen = openFloor === floorId;

              return (
                <div
                  key={floorId}
                  className={styles.floorCard}
                >
                  {/* HEADER */}
                  <div
                    onClick={() => {
                      const next = isOpen ? null : floorId;
                      setOpenFloor(next);

                      // reset merge
                      setMergeSelected(new Set());
                      setMergeFloorId(null);
                    }}
                    className={styles.floorHeader}
                  >
                    <span className={styles.floorTitle}>
                      {formatFloorName(floorId)}
                    </span>
                    <span>{isOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}</span>
                  </div>

                  {/* CONTENT */}
                  {isOpen && (
                    <div className={styles.units}>
                      {groupedFloors[floorId].map((u) => (
                        <div
                          key={u.flat_id}
                          onClick={() => handleOpenFlat(u)}
                          className={[
                            styles.unitChip,
                            u.unit_type === "Jodi"
                              ? styles.unitJodi
                              : u.is_merged
                              ? styles.unitMerged
                              : styles.unitNormal,
                            mergeSelected.has(u.flat_id) ? styles.selectedRing : "",
                          ]
                            .filter(Boolean)
                            .join(" ")}
                        >
                          <div>{u.flat_number} ({u.unit_type})</div>

                          {u.unit_type !== "Jodi" && !u.is_merged && (
                            <Button
                              variant={mergeSelected.has(u.flat_id) ? "primary" : "ghost"}
                              size="sm"
                              style={{ width: "22px", height: "22px", minWidth: "22px", padding: 0 }}
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleMergeSelection(u);
                              }}
                              icon={mergeSelected.has(u.flat_id) ? <Check size={12} strokeWidth={2.5} /> : <Plus size={12} strokeWidth={2.5} />}
                              title={mergeSelected.has(u.flat_id) ? "Selected for merge" : "Select for merge"}
                            />
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* MODALS */}
        <Modal
          isOpen={open}
          onClose={() => setOpen(false)}
          size="lg"
          title="Generate / Update Units"
        >
          <UnitForm
            towers={towers}
            societyId={selectedSocietyId}
            propertyType={selectedSociety?.society_type}
            onSuccess={fetchData}
          />
        </Modal>

        <Modal
          isOpen={flatModalOpen}
          onClose={() => setFlatModalOpen(false)}
          title="Flat Details"
        >
          {selectedFlat ? (
            <div>
              <p><strong>Flat:</strong> {selectedFlat.flat_number}</p>
              <p><strong>Type:</strong> {selectedFlat.unit_type}</p>
              <p><strong>Owner:</strong> {selectedFlat.owner_name || "N/A"}</p>
              <p><strong>Phone:</strong> {selectedFlat.phone || "N/A"}</p>
              <p><strong>Email:</strong> {selectedFlat.email || "N/A"}</p>

              {/* UNMERGE BUTTON */}
              {selectedFlat.unit_type === "Jodi" && (
                <div className={styles.divider}>
                  <Button
                    variant="danger"
                    onClick={async () => {
                      if (!window.confirm("Unmerge this unit?")) return;

                      try {
                        await unmergeUnits({
                          merged_unit_id: selectedFlat.merged_unit_id,
                        });

                        toast.success("Units unmerged");
                        setFlatModalOpen(false);
                        fetchData();
                      } catch (err) {
                        console.error(err);
                        toast.error("Unmerge failed");
                      }
                    }}
                  >
                    Unmerge Units
                  </Button>
                </div>
              )}
            </div>
          ) : (
            <div style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <div className={styles.unitSkeletonShimmer} style={{ width: '100%', height: '20px' }} />
              <div className={styles.unitSkeletonShimmer} style={{ width: '80%', height: '20px' }} />
              <div className={styles.unitSkeletonShimmer} style={{ width: '90%', height: '20px' }} />
              <div className={styles.unitSkeletonShimmer} style={{ width: '65%', height: '20px' }} />
            </div>
          )}
        </Modal>

        <Modal
          isOpen={importOpen}
          onClose={() => setImportOpen(false)}
          title="Bulk Import Units"
          className="max-w-2xl"
        >
          <BulkImport
            onSuccess={() => {
              setImportOpen(false);
              fetchData();
            }}
          />
        </Modal>

      </div>
    </AdminLayout>

  );
};

export default UnitManagement;
